/**
 * Shared survey-aggregation lib (R4.1 / crafted-exhibits Step 1).
 *
 * Schema traversal lives here, not in `components/charts/`. Both
 * `apps/frontend/src/lib/utils/aggregation.ts` and
 * `packages/deck-ui/src/components/charts/chart-aggregation.ts` re-export this
 * module. App barrel signatures are unchanged for every live importer.
 *
 * Named winners (app fork):
 *   - getQuestionValue keeps attributeAliases
 *   - aggregateGrouped keys segments by declared semantics (segmentBinning.ts)
 *   - row-label resolution keeps case-insensitive + text-match fallback
 *     (via survey-answer-format.ts, moved here with this merge)
 *   - aggregateSimple keeps the app's empty-drop + dontKnowCount semantics
 *     (optional `includeEmpty` remains so deck-ui chart callers that already
 *     pass it keep compiling; default path is empty-drop)
 *
 * Accepted-diff items:
 *   - D6 NaN-bin: a zero-range high-cardinality set (many string spellings of
 *     1.5) must yield a finite bin instead of NaN edges → throw. Never carry
 *     the throw forward. Now held by `utils/continuousBinning`, which falls
 *     back to the smallest allowed step on a span of zero.
 *
 * Zero-importer deletions (not ported as public exports):
 *   DONT_KNOW_VALUES, AggregationResult, GroupedDataPoint,
 *   isOpenTextMatrixColumn.
 *   MatrixDropdownDataPoint / MatrixDropdownAggregationResult stay exported
 *   because deck-ui chart callers already import them from chart-aggregation.
 *   aggregateSimpleWithStats was listed as a zero-importer at characterization
 *   time; the merge contract exports it and D5 pins it — kept.
 *
 * Floor constants: this module is the home of CUT_SUPPRESS_N (5) and
 * CUT_DIRECTIONAL_N (15). `divergingStacked.ts` re-exports them. Do not mint
 * a third pair. `quickStudyPure.ts` still duplicates the same numbers as
 * SEAM_* literals (R1.1 cycle with divergingStacked — left there).
 *
 * Explicitly deferred (not this merge):
 *   - apps/workflow-console/src/lib/surveyResults/aggregation.ts
 *   - packages/deck-ui/src/components/charts/diverging/divergingLayout.ts
 *   - packages/deck-ui/src/components/charts/diverging/divergingBands.ts
 *
 * Dead `survey-core` import dropped before this landed in deck-ui.
 */

import { isMissingAnswerSentinel, isMissingCategoricalAnswer } from '@digital-twins/shared-types'

import { roundHalfAwayFromZero, roundHalfAwayFromZeroAt } from '../foundations/producer-rounding'

import { resolveChoiceLabel, resolveRowLabel, resolveColumnLabel, resolveCellValueLabel } from './survey-answer-format'
import { buildSegmentKeyer } from './segmentBinning'
import {
  binCounts,
  continuousBinEdges,
  continuousBinLabels,
  readContinuousDomain,
  type ContinuousBin,
} from '../utils/continuousBinning'


/**
 * A dict answer — legal for ranking, matrix, and twin-monadic lanes.
 *
 * Scope is never inferred from this shape (plan decision 3). Aggregators that
 * would `String(dict)` into one bar labelled `[object Object]` must skip it
 * and let the caller split by `monadicLaneScope` first.
 */
export function isPlainObjectAnswer(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The option values a SurveyJS question declares: `choices` on select
 * questions, `columns` on a simple matrix. An exact declared value is an
 * answer even when it is spelled like a sentinel ("None", "N/A").
 */
function declaredAnswerValues(question: any): Array<unknown> {
  const declared = Array.isArray(question?.choices)
    ? question.choices
    : question?.type === 'matrix' && Array.isArray(question?.columns)
      ? question.columns
      : []
  return declared.map((item: any) => (item !== null && typeof item === 'object' ? item.value : item))
}

/**
 * Check if a value should be excluded from calculations: null, or a
 * missing-answer sentinel from the canonical list
 * (`MISSING_ANSWER_SENTINELS`, generated from canonical_analysis/runtime.py).
 * Pass the question so a declared option spelled like a
 * sentinel counts as an answer. Zero is always an answer.
 */
export function isDontKnowValue(value: any, question?: any): boolean {
  if (value === null || value === undefined) {
    return true
  }
  if (isPlainObjectAnswer(value)) {
    return false
  }
  // Non-strings keep their string reading: 0 stays "0" (an answer), and an
  // array keeps its joined form (`['na']` is missing, `[]` is empty).
  const candidate = typeof value === 'string' ? value : String(value)
  if (question === undefined) {
    return isMissingAnswerSentinel(candidate)
  }
  return isMissingCategoricalAnswer(candidate, declaredAnswerValues(question))
}

/**
 * Whether one respondent contributed a usable answer, counting a per-asset or
 * matrix map once and a selection list once. The browser implementation of
 * canonical `respondent_answered` (canonical_analysis/runtime.py) for
 * interactive filters; vectors: tests/fixtures/reporting/base-eligibility.v1.json.
 * Pass the question so a declared option
 * spelled like a sentinel counts.
 */
export function respondentAnswered(value: unknown, question?: any): boolean {
  if (value === null || value === undefined) {
    return false
  }
  if (Array.isArray(value)) {
    return value.some((item) => respondentAnswered(item, question))
  }
  if (isPlainObjectAnswer(value)) {
    return Object.values(value).some((item) => respondentAnswered(item, question))
  }
  return !isDontKnowValue(value, question)
}

/**
 * The declaration a matrix-dropdown cell answers against: its column's
 * `choices`, else the question's (SurveyJS falls back the same way). Pass the
 * result to `isDontKnowValue` so a declared cell choice spelled like a
 * sentinel ("None", "N/A") stays an answer.
 */
export function matrixDropdownCellDeclaration(question: any, column: any): { choices: Array<unknown> } {
  if (Array.isArray(column?.choices)) {
    return { choices: column.choices }
  }
  return { choices: Array.isArray(question?.choices) ? question.choices : [] }
}

export interface ChartDataPoint {
  name: string
  value: number
  /** Exact membership of a numeric option; labels are presentation only. */
  numericBin?: ContinuousBin & { inclusiveHigh: boolean }
  [key: string]: any // For grouped data (e.g., { name: 'Male', 'Coffee': 10, 'Tea': 5 })
}

interface AggregationResult {
  data: ChartDataPoint[]
  dontKnowCount: number
  dontKnowPercent: number
  totalResponses: number
}

/** Cut n below this is suppressed from the plot (named in the warning line). */
export const CUT_SUPPRESS_N = 5
/** Cut n below this is directional-only. */
export const CUT_DIRECTIONAL_N = 15

/**
 * Identifies questions that can be used for segmentation (filters)
 */
export function detectSegmentableQuestions(survey: any): any[] {
  if (!survey || !survey.pages) return []

  const SEGMENTABLE_TYPES = ['radiogroup', 'dropdown', 'boolean', 'checkbox', 'rating', 'ranking']

  const questions: any[] = []

  function collectFromElements(elements: any[]) {
    for (const element of elements) {
      if ((element.type === 'panel' || element.type === 'paneldynamic') && Array.isArray(element.elements)) {
        collectFromElements(element.elements)
      } else if (SEGMENTABLE_TYPES.includes(element.type)) {
        questions.push(element)
      }
    }
  }

  survey.pages.forEach((page: any) => {
    if (page.elements) {
      collectFromElements(page.elements)
    }
  })
  return questions
}

/**
 * Resolve a question value across all known response shapes.
 * Supports survey questions and synthetic population-attribute questions.
 *
 * Synthetic population-attribute questions may declare `attributeAliases`: extra
 * row keys the same attribute is known to be written under (e.g. a runtime that
 * stamps both snake_case and camelCase spellings). Aliases are tried after the
 * question's own name and title, in every attribute container, so a row carrying
 * the value at the top level resolves identically to one carrying it nested.
 */
export function getQuestionValue(response: any, question: any): any {
  if (!response || !question) return undefined

  const readFrom = (container: any, key?: string): any => {
    if (!container || typeof container !== 'object' || !key) return undefined
    return container[key]
  }

  if (question.isPopulationAttribute) {
    const attributeContainers = [
      response.respondent_attributes,
      response.population_attributes,
      response.attributes,
      response,
    ]

    const aliases: Array<string> = Array.isArray(question.attributeAliases)
      ? question.attributeAliases.filter((alias: unknown) => typeof alias === 'string')
      : []
    const attributeKeys: Array<string | undefined> = [
      question.name,
      question.title,
      ...aliases,
    ]

    for (const container of attributeContainers) {
      for (const key of attributeKeys) {
        const value = readFrom(container, key)
        if (value !== undefined) return value
      }
    }

    return undefined
  }

  const responseContainers = [
    response.survey_data,
    response.answers,
    response.response,
    response,
  ]

  for (const container of responseContainers) {
    const byName = readFrom(container, question.name)
    if (byName !== undefined) return byName

    const byTitle = readFrom(container, question.title)
    if (byTitle !== undefined) return byTitle
  }

  return undefined
}

/**
 * Resolve one response into its explicit memberships for a question.
 * Checkbox values are arrays in current artifacts; the comma-separated branch
 * preserves the legacy multiselect shape already supported by aggregateSimple.
 */
export function getQuestionValues(response: any, question: any): any[] {
  const value = getQuestionValue(response, question)
  const values = Array.isArray(value)
    ? value
    : question?.isMultiselect && typeof value === 'string'
      ? value.split(',').map((item) => item.trim())
      : [value]

  return values.filter(
    (item) =>
      item !== null &&
      item !== undefined &&
      // A dict answer (ranking, matrix, twin-monadic lane) has no single
      // membership. `String(dict)` would become one group labelled
      // `[object Object]`, so it is skipped here and the caller splits by
      // `monadicLaneScope` first.
      !isPlainObjectAnswer(item) &&
      String(item).trim() !== '',
  )
}

/**
 * Aggregates data for a simple (non-grouped) chart
 * Automatically bins numeric data if high cardinality (> 15 unique values)
 * Excludes "don't know" / N/A values from calculations and tracks them separately
 */
export function aggregateSimple(
  question: any,
  responses: any[],
  includeEmpty = false,
): ChartDataPoint[] {
  if (!responses || responses.length === 0) return []

  // Collect all valid values first, tracking "don't know" separately
  const rawValues: any[] = []
  let dontKnowCount = 0
  const totalResponses = responses.length

  responses.forEach((r: any) => {
    const value = getQuestionValue(r, question)
    if (value !== undefined && value !== null && value !== '') {
      if (isPlainObjectAnswer(value)) {
        return
      }
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (isPlainObjectAnswer(v)) {
            return
          }
          if (isDontKnowValue(v, question)) {
            dontKnowCount++
          } else {
            rawValues.push(v)
          }
        })
      } else if (question?.isMultiselect && typeof value === 'string') {
        // Backward compatibility: legacy populations may store multiselect
        // answers as comma-separated strings.
        value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0)
          .forEach((v) => {
            if (isDontKnowValue(v, question)) {
              dontKnowCount++
            } else {
              rawValues.push(v)
            }
          })
      } else {
        if (isDontKnowValue(value, question)) {
          dontKnowCount++
        } else {
          rawValues.push(value)
        }
      }
    }
  })

  if (rawValues.length === 0) return []

  // Check cardinality and type
  const uniqueValues = new Set(rawValues.map(v => String(v)))
  const cardinality = uniqueValues.size

  // Heuristic for numeric binning:
  // 1. High cardinality (> 15)
  // 2. Looks numeric (check a sample)
  // 3. Question type supports it (or if it's population attribute)
  const isLikelyNumeric = rawValues.slice(0, 50).every(v => !isNaN(Number(v)))

  // Force binning for known high-cardinality types if numeric
  const shouldBin = isLikelyNumeric && cardinality > 15

  if (shouldBin) {
    const numericValues = rawValues.map(v => Number(v)).sort((a, b) => a - b)

    // D9: one binning policy for every continuous distribution drawn in TypeScript,
    // ported from `canonical_analysis/continuous_statistics.py` and asserted against
    // the same vectors from pytest and vitest. Superseded here: a local `getNiceStep`
    // targeting 8 bins with no tail cut, no bin ceiling and no notion of a bounded
    // unit, which drew a different grid from the one the deck drew for the same
    // answers. The D6 NaN-bin guard it carried is now inside the utility (a span of
    // zero falls back to the smallest allowed step), so a zero-range high-cardinality
    // set still yields finite edges instead of throwing.
    const binning = continuousBinEdges(numericValues, readContinuousDomain(question))
    const counts = binCounts(numericValues, binning.edges)
    const labels = continuousBinLabels(binning)

    // Edge bins are no longer trimmed: the left edge is floored onto the grid and the
    // right edge holds the maximum, so an empty edge bin now only happens on a bounded
    // unit, where the empty stretch of the declared domain IS the finding.
    return binning.edges.map((edge, index) => ({
      name: labels[index],
      value: counts[index],
      rawValue: edge.low,
      numericBin: { ...edge, inclusiveHigh: index === binning.edges.length - 1 },
    }))
  }

  // Standard categorical counting (existing logic)
  const counts: Record<string, number> = {}

  // Seed every declared choice at 0 so a head-to-head shows the losing/0% option too
  // (otherwise a unanimous pick renders a single lonely bar). Opt-in; off for reports
  // and for the app empty-drop default (named winner).
  if (includeEmpty && Array.isArray(question?.choices)) {
    question.choices.forEach((c: any) => {
      const key = String(c?.value ?? c)
      if (key) counts[key] = 0
    })
  }

  rawValues.forEach(val => {
    const strVal = String(val)
    counts[strVal] = (counts[strVal] || 0) + 1
  })

  if (Object.keys(counts).length === 0) return []

  // Map to ChartDataPoint
  // Use choices to get proper labels if available
  // Check both value AND text fields since responses might use either format
  // Use case-insensitive matching to handle variations like 'brand_0' vs 'Brand_0'
  const dataPoints: ChartDataPoint[] = Object.entries(counts).map(([val, count]) => {
    const label = resolveChoiceLabel(question, val)
    return {
      name: label,
      value: count,
      rawValue: val
    }
  })

  // Sort by count desc (for categorical) or value (if numeric-like but low cardinality)
  if (isLikelyNumeric) {
    return dataPoints.sort((a, b) => Number(a.rawValue) - Number(b.rawValue))
  }
  return dataPoints.sort((a, b) => b.value - a.value)
}

/**
 * Aggregates data for a grouped chart (segmentation)
 * Transposed: Group by Target Question Options (X-Axis), Stack by Segment Options
 */
export interface GroupedAggregationResult {
  data: ChartDataPoint[]
  /** Respondents with a target answer, counted once in each segment membership. */
  segmentBases: Record<string, number>
}

export function aggregateGroupedWithBases(
  targetQuestion: any,
  groupQuestion: any,
  responses: any[]
): GroupedAggregationResult {
  if (!responses || responses.length === 0) return { data: [], segmentBases: {} }

  // Map of Target Option -> { Segment Option -> Count }
  const targetMap: Record<string, Record<string, number>> = {}
  const segmentBases: Record<string, number> = {}

  // Declared semantics only: a declared age attribute
  // uses the declared age bands, a declared continuous question uses the one
  // continuous binning policy, anything else keys on the raw value.
  const segmentKey = buildSegmentKeyer(
    groupQuestion,
    responses.flatMap((r: any) => getQuestionValues(r, groupQuestion)),
  )

  responses.forEach((r: any) => {
    // Usable target answers only: a saved sentinel is not
    // drawn as an option or counted in the segment base, and a declared option
    // spelled like one ("None", "N/A") is, as in aggregateSimple and canonical
    // analysis. Before, "n/a" was drawn under a declared "N/A" label by the
    // case-insensitive label match.
    const normalizedTargetValues = getQuestionValues(r, targetQuestion).filter(
      (value) => !isDontKnowValue(value, targetQuestion),
    )
    const normalizedGroupValues = getQuestionValues(r, groupQuestion)
    if (normalizedTargetValues.length === 0 || normalizedGroupValues.length === 0) return

    const processValue = (val: any, segmentVal: any) => {
      if (isPlainObjectAnswer(val) || isPlainObjectAnswer(segmentVal)) return
      const targetStr = String(val) // Target Value (X-Axis Key)
      const segmentStr = segmentKey(segmentVal)

      if (!targetMap[targetStr]) {
        targetMap[targetStr] = {}
      }

      targetMap[targetStr][segmentStr] = (targetMap[targetStr][segmentStr] || 0) + 1
    }

    const uniqueTargetValues = Array.from(new Set(normalizedTargetValues.map((value) => String(value))))
    const uniqueSegmentValues = Array.from(new Set(normalizedGroupValues.map(segmentKey)))

    uniqueTargetValues.forEach((targetValue) => {
      uniqueSegmentValues.forEach((groupValue) => processValue(targetValue, groupValue))
    })
    uniqueSegmentValues.forEach((groupValue) => {
      const segmentLabel = resolveChoiceLabel(groupQuestion, groupValue)
      segmentBases[segmentLabel] = (segmentBases[segmentLabel] || 0) + 1
    })
  })

  // Transform to Recharts format
  // We iterate over Target Options (X-Axis categories)
  const groupedData = Object.entries(targetMap).map(([targetOption, counts]) => {
    const targetLabel = resolveChoiceLabel(targetQuestion, targetOption)

    const point: ChartDataPoint = {
      name: targetLabel,
      value: 0 // Will hold total count for this target option
    }

    // Fill in counts for each segment option (Stack Keys)
    Object.entries(counts).forEach(([segmentVal, count]) => {
      const segmentLabel = resolveChoiceLabel(groupQuestion, segmentVal)

      point[segmentLabel] = count
      point.value += count
    })

    return point
  })

  // Seed declared group choices at 0 so a segment with no answers for this
  // question still appears instead of vanishing from the chart.
  if (Array.isArray(groupQuestion?.choices)) {
    const declaredSegmentValues: string[] = groupQuestion.choices
      .map((choice: any) => String(choice?.value ?? choice).trim())
      .filter(Boolean)

    groupedData.forEach((point) => {
      declaredSegmentValues.forEach((segmentValue) => {
        const segmentLabel = resolveChoiceLabel(groupQuestion, segmentValue)
        if (point[segmentLabel] === undefined) {
          point[segmentLabel] = 0
        }
        if (segmentBases[segmentLabel] === undefined) {
          segmentBases[segmentLabel] = 0
        }
      })
    })
  }

  const data = groupedData.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value
    return String(a.name).localeCompare(String(b.name))
  })

  return { data, segmentBases }
}

export function aggregateGrouped(
  targetQuestion: any,
  groupQuestion: any,
  responses: any[],
): ChartDataPoint[] {
  return aggregateGroupedWithBases(targetQuestion, groupQuestion, responses).data
}

export interface MatrixAggregationResult {
  data: ChartDataPoint[]
  skipCounts: Record<string, number>  // Per-row skip counts
  totalResponses: number
}

/**
 * Aggregates Matrix data (simple matrix with single rating per row)
 * Returns: { data, skipCounts, totalResponses }
 * Excludes "don't know" / N/A values from calculations but tracks them
 */
export function aggregateMatrix(question: any, responses: any[]): MatrixAggregationResult {
  if (!responses || !question.rows) {
    return { data: [], skipCounts: {}, totalResponses: 0 }
  }

  const rowMap: Record<string, Record<string, number>> = {}
  const rowNaCount: Record<string, number> = {}  // Track N/A counts per row

  // Initialize rows
  question.rows.forEach((row: any) => {
    const rowVal = row.value !== undefined ? row.value : row
    rowMap[rowVal] = {}
    rowNaCount[rowVal] = 0
  })

  responses.forEach((r: any) => {
    // Pass full response object to getValue
    const matrixData = getQuestionValue(r, question)

    if (!matrixData) return

    Object.entries(matrixData).forEach(([rowKey, colVal]) => {
      if (!rowMap[rowKey]) {
        rowMap[rowKey] = {} // Handle dynamic rows if any
        rowNaCount[rowKey] = 0
      }

      // Skip N/A values from main aggregation but track them
      if (isDontKnowValue(colVal, question)) {
        rowNaCount[rowKey] = (rowNaCount[rowKey] || 0) + 1
        return
      }

      const colStr = String(colVal)
      rowMap[rowKey][colStr] = (rowMap[rowKey][colStr] || 0) + 1
    })
  })

  const data = Object.entries(rowMap).map(([rowKey, colCounts]) => {
    const point: ChartDataPoint = {
      name: resolveRowLabel(question, rowKey),
      value: 0,
      _rowKey: rowKey
    }

    Object.entries(colCounts).forEach(([colVal, count]) => {
      const colLabel = resolveColumnLabel(question, colVal)
      point[colLabel] = count
      point.value += count
    })

    return point
  })

  // Build skip counts with resolved labels
  const skipCountsWithLabels: Record<string, number> = {}
  Object.entries(rowNaCount).forEach(([rowKey, count]) => {
    const dataPoint = data.find(d => d._rowKey === rowKey)
    if (dataPoint) {
      skipCountsWithLabels[dataPoint.name] = count
    }
  })

  return {
    data,
    skipCounts: skipCountsWithLabels,
    totalResponses: responses.length
  }
}

/**
 * Data structure for matrixdropdown aggregation
 */
export interface MatrixDropdownDataPoint {
  rowName: string       // Row label (e.g., "Deel")
  rowValue: string      // Row value (e.g., "brand_0")
  columns: {
    name: string        // Column label (e.g., "Customer Service")
    value: string       // Column value (e.g., "attr_0")
    kind: 'numeric' | 'categorical' | 'empty'
    avgRating: number | null   // Average rating across all responses (numeric only)
    count: number       // Number of responses
    skipCount: number   // Number of skipped (N/A) responses
    distribution: Record<string, number>  // Label -> count distribution
    distributionItems: { value: string; label: string; count: number }[]
    topValue: { value: string; label: string; count: number; percentage: number } | null
  }[]
}

export interface MatrixDropdownAggregationResult {
  data: MatrixDropdownDataPoint[]
  totalResponses: number
}

function isOpenTextMatrixColumn(column: any): boolean {
  const cellType = String(column?.cellType || column?.inputType || '').toLowerCase()
  if (cellType) {
    return cellType === 'text' || cellType === 'comment'
  }

  const fallbackType = String(column?.type || '').toLowerCase()
  return fallbackType === 'comment'
}

export function getChartableMatrixColumns(question: any): any[] {
  if (!Array.isArray(question?.columns)) {
    return []
  }

  return question.columns.filter((column: any) => !isOpenTextMatrixColumn(column))
}

export function isNumericMatrixColumn(column: any, question?: any): boolean {
  const cellType = String(column?.cellType || question?.cellType || '').toLowerCase()
  if (cellType === 'rating') {
    return true
  }

  const choices = Array.isArray(column?.choices)
    ? column.choices
    : Array.isArray(question?.choices)
      ? question.choices
      : []

  return choices.some((choice: any) => {
    const value = Number(choice?.value ?? choice)
    return Number.isFinite(value)
  })
}

export function getNumericMatrixColumns(question: any): any[] {
  if (!Array.isArray(question?.columns)) {
    return []
  }

  return question.columns.filter((column: any) => isNumericMatrixColumn(column, question))
}

/**
 * Aggregates MatrixDropdown data (2D matrix with rating per row per column)
 * Used for questions like "Rate each brand on each attribute"
 * 
 * Response format: { brand_0: { attr_0: 4, attr_1: 3 }, brand_1: { attr_0: 5, attr_1: 2 } }
 * Returns structured data for 2D heatmap visualization
 */
export function aggregateMatrixDropdown(question: any, responses: any[]): MatrixDropdownAggregationResult {
  if (!responses || !question.rows || !question.columns) {
    return { data: [], totalResponses: 0 }
  }

  // Collect values and skip counts: { rowKey: { colKey: { values: [], skipCount: 0 } } }
  const valueMap: Record<string, Record<string, { values: string[]; skipCount: number }>> = {}
  const declarationByColumn: Record<string, { choices: Array<unknown> }> = {}
  question.columns.forEach((col: any) => {
    const colVal = col.name || col.value || col
    declarationByColumn[colVal] = matrixDropdownCellDeclaration(question, col)
  })

  // Initialize from question definition
  question.rows.forEach((row: any) => {
    const rowVal = row.value !== undefined ? row.value : row
    valueMap[rowVal] = {}
    question.columns.forEach((col: any) => {
      const colVal = col.name || col.value || col
      valueMap[rowVal][colVal] = { values: [], skipCount: 0 }
    })
  })

  // Process responses
  responses.forEach((r: any) => {
    const matrixData = getQuestionValue(r, question)

    if (!matrixData || typeof matrixData !== 'object') return

    // matrixData format: { brand_0: { attr_0: 4, attr_1: 3 }, ... }
    Object.entries(matrixData).forEach(([rowKey, colRatings]) => {
      if (!valueMap[rowKey]) {
        valueMap[rowKey] = {}
      }

      if (typeof colRatings === 'object' && colRatings !== null) {
        Object.entries(colRatings as Record<string, any>).forEach(([colKey, rating]) => {
          if (!valueMap[rowKey][colKey]) {
            valueMap[rowKey][colKey] = { values: [], skipCount: 0 }
          }

          // Track N/A values separately. An undeclared column still reads the
          // question's choices, as SurveyJS does.
          const declaration = declarationByColumn[colKey] ?? matrixDropdownCellDeclaration(question, undefined)
          if (isDontKnowValue(rating, declaration)) {
            valueMap[rowKey][colKey].skipCount++
            return
          }

          valueMap[rowKey][colKey].values.push(String(rating))
        })
      }
    })
  })

  // Build output
  const result: MatrixDropdownDataPoint[] = []

  Object.entries(valueMap).forEach(([rowKey, colData]) => {
    const columns = Object.entries(colData).map(([colKey, data]) => {
      const { values, skipCount } = data

      const distributionMap: Record<string, { value: string; label: string; count: number }> = {}
      values.forEach((rawValue) => {
        const value = String(rawValue)
        if (!distributionMap[value]) {
          distributionMap[value] = {
            value,
            label: resolveCellValueLabel(question, colKey, value),
            count: 0,
          }
        }
        distributionMap[value].count++
      })

      const distributionItems = Object.values(distributionMap).sort((a, b) => b.count - a.count)
      const distribution: Record<string, number> = {}
      distributionItems.forEach((item) => {
        distribution[item.label] = item.count
      })

      const numericValues = values.map((v) => Number(v))
      const isNumeric = values.length > 0 && numericValues.every((v) => Number.isFinite(v))
      const avgRating = isNumeric && numericValues.length > 0
        ? roundHalfAwayFromZeroAt(numericValues.reduce((a, b) => a + b, 0) / numericValues.length, 2)
        : null
      const topValue = distributionItems.length > 0
        ? {
          value: distributionItems[0].value,
          label: distributionItems[0].label,
          count: distributionItems[0].count,
          percentage: values.length > 0 ? roundHalfAwayFromZero((distributionItems[0].count / values.length) * 100) : 0,
        }
        : null

      return {
        name: resolveColumnLabel(question, colKey),
        value: colKey,
        kind: (values.length === 0 ? 'empty' : isNumeric ? 'numeric' : 'categorical') as 'numeric' | 'categorical' | 'empty',
        avgRating,
        count: values.length,
        skipCount,
        distribution,
        distributionItems,
        topValue,
      }
    })

    result.push({
      rowName: resolveRowLabel(question, rowKey),
      rowValue: rowKey,
      columns
    })
  })

  return {
    data: result,
    totalResponses: responses.length
  }
}

/**
 * Check if a question is a matrixdropdown type
 */
export function isMatrixDropdown(question: any): boolean {
  return question.type === 'matrixdropdown' || question.type === 'matrixdynamic'
}

/**
 * Aggregates data for a simple chart and returns N/A statistics
 */
export function aggregateSimpleWithStats(question: any, responses: any[]): AggregationResult {
  if (!responses || responses.length === 0) {
    return { data: [], dontKnowCount: 0, dontKnowPercent: 0, totalResponses: 0 }
  }

  const totalResponses = responses.length
  let dontKnowCount = 0

  // Count don't know values
  responses.forEach((r: any) => {
    const value = getQuestionValue(r, question)
    if (isDontKnowValue(value, question)) {
      dontKnowCount++
    }
  })

  const data = aggregateSimple(question, responses)
  // Browser-only figure: no producer publishes a don't-know share, so the
  // producer rounding rule does not govern it and it keeps half-up rounding.
  const dontKnowPercent = totalResponses > 0 ? Math.round((dontKnowCount / totalResponses) * 100) : 0

  return {
    data,
    dontKnowCount,
    dontKnowPercent,
    totalResponses
  }
}
